/**
 * ExploreJourneys — interactive "Explore user journeys" (Plausible-style path
 * exploration), rendered as the EXPLORE tab in the Goals section.
 *
 * Fetches per-session ordered page sequences from /analytics/{id}/journeys
 * (which honors all global filters + public-mode URL sanitization), then builds
 * the 5-step drill-down client-side:
 *   Starting point → 1 step after → … → 5 steps after
 * Each column shows the next-step distribution for the selected path prefix,
 * a funnel-retention % in its header, "No further action" for sessions that
 * ended, and a 📁/📄 folder indicator. The header shows the full path's CR.
 *
 * Mirrors the standalone demo (Desktop/user-journeys-demo.html) one-to-one.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Flag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_STEPS = 5;

const ExploreJourneys = ({
	websiteId,
	token,
	period = '24h',
	startDate,
	endDate,
	channel,
	location,
	device,
	os,
	browser,
	goal,
	existingFilters = [],
}) => {
	const [sequences, setSequences] = useState([]);
	const [folderSet, setFolderSet] = useState(() => new Set());
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selection, setSelection] = useState([]); // chosen path prefix

	const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
	const filtersKey = useMemo(() => existingFilters.join('|'), [existingFilters]);

	// Build the /journeys URL with the standard global filters.
	const buildUrl = () => {
		let url = `${API}/analytics/${websiteId}/journeys?period=${period}`;
		if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
		if (channel) url += `&channel=${encodeURIComponent(channel)}`;
		if (location) url += `&location=${encodeURIComponent(location)}`;
		if (device) url += `&device=${encodeURIComponent(device)}`;
		if (os) url += `&os=${encodeURIComponent(os)}`;
		if (browser) url += `&browser=${encodeURIComponent(browser)}`;
		if (goal) url += `&goal=${encodeURIComponent(goal)}`;
		existingFilters.forEach((fl) => { url += `&f=${encodeURIComponent(fl)}`; });
		return url;
	};

	const fetchJourneys = async (signal) => {
		if (!websiteId) return;
		setLoading(true);
		setError(null);
		try {
			const res = await axios.get(buildUrl(), { headers: authHeaders, signal });
			setSequences(res.data?.sequences || []);
			setFolderSet(new Set(res.data?.folders || []));
			setSelection([]); // reset drill-down when the dataset changes
		} catch (err) {
			if (axios.isCancel(err)) return;
			setError('Failed to load user journeys');
			setSequences([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const ctrl = new AbortController();
		fetchJourneys(ctrl.signal);
		return () => ctrl.abort();
		// eslint-disable-next-line
	}, [websiteId, token, period, startDate, endDate, channel, location, device, os, browser, goal, filtersKey]);

	const total = sequences.length;

	const isFolder = (path) => folderSet.has(path);

	// Columns are ranked by visitor count, highest first (standard path-explorer
	// ordering — most-traveled paths surface at the top).

	// Starting-point distribution (first page of each session).
	const startDist = useMemo(() => {
		const counts = {};
		for (const s of sequences) counts[s[0]] = (counts[s[0]] || 0) + 1;
		return Object.entries(counts)
			.map(([path, count]) => ({ path, count }))
			.sort((a, b) => b.count - a.count);
	}, [sequences]);

	// Next-step distribution for a given path prefix.
	const stepDist = (prefix) => {
		const depth = prefix.length;
		let matched = 0;
		let terminal = 0;
		const counts = {};
		for (const s of sequences) {
			let ok = true;
			for (let i = 0; i < depth; i++) {
				if (s[i] !== prefix[i]) { ok = false; break; }
			}
			if (!ok) continue;
			matched++;
			if (s.length <= depth) { terminal++; continue; }
			const nxt = s[depth];
			counts[nxt] = (counts[nxt] || 0) + 1;
		}
		const items = Object.entries(counts)
			.map(([path, count]) => ({ path, count }))
			.sort((a, b) => b.count - a.count);
		return { matched, terminal, items };
	};

	const startCohort = selection.length === 0
		? total
		: sequences.filter((s) => s[0] === selection[0]).length;

	// CR for the full selected path.
	const selectedReached = useMemo(() => {
		if (selection.length === 0) return 0;
		return sequences.filter((s) => {
			for (let i = 0; i < selection.length; i++) if (s[i] !== selection[i]) return false;
			return true;
		}).length;
	}, [sequences, selection]);

	const pickStep = (depth, path) => {
		const next = selection.slice(0, depth);
		next[depth] = path;
		setSelection(next);
	};

	// Assemble the columns to render.
	const columns = [];
	columns.push({ title: 'Starting point', pct: '100%', items: startDist, depth: 0, terminal: null });
	for (let depth = 1; depth <= MAX_STEPS; depth++) {
		const prefix = selection.slice(0, depth);
		if (prefix.length < depth) break; // nothing chosen yet at this depth
		const dist = stepDist(prefix);
		const pct = startCohort ? `${((dist.matched / startCohort) * 100).toFixed(1)}%` : '0%';
		columns.push({
			title: `${depth} step${depth > 1 ? 's' : ''} after`,
			pct,
			items: dist.items,
			depth,
			terminal: dist.terminal,
		});
	}

	const cr = total ? ((selectedReached / total) * 100).toFixed(1) : '0';

	return (
		<div className="explore-journeys">
			<div className="explore-journeys__head">
				<div>
					<h3 className="explore-journeys__title">
						{selection.length > 0 ? `${selection.length}-step user journey` : 'Explore user journeys'}
					</h3>
					<p className="explore-journeys__sub">Click a step to see where visitors went next</p>
				</div>
				<div className="explore-journeys__head-right">
					{selection.length > 0 && (
						<span className="explore-journeys__cr">CR: <b>{cr}%</b> ({selectedReached})</span>
					)}
					<button
						type="button"
						className="explore-journeys__refresh"
						title="Reset / refresh"
						onClick={() => setSelection([])}
					>
						<RefreshCw size={15} />
					</button>
				</div>
			</div>

			{loading ? (
				<div className="explore-journeys__state">Loading user journeys…</div>
			) : error ? (
				<div className="explore-journeys__state">{error}</div>
			) : total === 0 ? (
				<div className="explore-journeys__state">No journey data for the selected period and filters.</div>
			) : (
				<div className="explore-cols">
					{columns.map((col) => {
						const max = Math.max(1, ...col.items.map((i) => i.count), col.terminal || 0);
						return (
							<div className="explore-col" key={col.title}>
								<div className="explore-col__head">
									<span className="explore-col__title">{col.title}</span>
									<span className="explore-col__pct">{col.pct}</span>
								</div>

								{col.terminal !== null && col.terminal > 0 && (
									<div className="explore-row explore-row--term">
										<span className="explore-row__bar" style={{ width: `${(col.terminal / max) * 100}%` }} />
										<span className="explore-row__left">
											<span className="explore-row__ico">🚪</span>
											<span className="explore-row__label">No further action</span>
										</span>
										<span className="explore-row__val">{col.terminal.toLocaleString()}</span>
									</div>
								)}

								{col.items.length === 0 && (col.terminal === null || col.terminal === 0) ? (
									<div className="explore-col__empty">
										<Flag size={18} />
										<span>No further steps found for the selected period and filters</span>
									</div>
								) : (
									col.items.map((it) => (
										<div
											key={it.path}
											className={`explore-row ${selection[col.depth] === it.path ? 'is-sel' : ''}`}
											onClick={() => pickStep(col.depth, it.path)}
										>
											<span className="explore-row__bar" style={{ width: `${(it.count / max) * 100}%` }} />
											<span className="explore-row__left">
												<span className="explore-row__ico">{isFolder(it.path) ? '📁' : '📄'}</span>
												<span className="explore-row__label" title={it.path}>{it.path}</span>
											</span>
											<span className="explore-row__val">{it.count.toLocaleString()}</span>
										</div>
									))
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default ExploreJourneys;
